import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  course_courseDetail_loading,
  course_courseDetail_error,
  course_courseDetail_dataError,
  course_title
} from '@paraglide/messages';
import { useLocation, useParams } from 'react-router';
import { usePrefixedNavigate } from '@edusense/navigation-utils';
import { useQueryClient } from '@tanstack/react-query';
import CourseDetailTemplate from '../template/CourseDetailTemplate';
import { shouldClearOwnedOverride } from '../template/courseOwnershipState';

import {
  useGetCourseDetail,
  type CourseDetail,
  type CourseDetailSection,
  getGetCourseDetailQueryKey
} from '@edusense/course-client';
import { LoadingSpinner as LoadingScreen, ErrorDisplay } from '@edusense/shared-ui-kit';
import { PageHeader } from '@edusense/shared-ui-layout';
import { useParaglideLocale } from '@edusense/shared-i18n';
// Zustand 스토어 import
import { useNavigationStore, NavigationState } from '../../store/navigationStore';

const LANGUAGE_STATE_SESSION_KEY_PREFIX = 'course_edit_language_state_';
const LANGUAGE_TRANSLATIONS_SESSION_KEY_PREFIX = 'course_edit_language_translations_';
const CURRICULUM_SNAPSHOT_SESSION_KEY_PREFIX = 'course_edit_curriculum_snapshot_';

type PersistedTranslationEntry = {
  title?: string;
  description?: string | null;
};

type PersistedLesson = {
  id?: string | number;
  title?: string;
  order?: number;
  duration?: number | null;
  contentType?: string | null;
  platformSupport?: string | null;
};

type PersistedSection = {
  id?: string | number;
  title?: string;
  description?: string | null;
  order?: number;
  lessons?: PersistedLesson[];
};

const normalizeLanguageCode = (code?: string | null): string | null => {
  if (!code) {
    return null;
  }
  const trimmed = `${code}`.trim();
  if (!trimmed) {
    return null;
  }
  return trimmed.toLowerCase();
};

const coerceLanguageCode = (code?: string | null): string => normalizeLanguageCode(code) ?? 'ko';

const loadPersistedLanguageState = (
  courseId: string,
): { currentLanguage?: string; supportedLanguages?: string[] } | null => {
  try {
    const raw = sessionStorage.getItem(`${LANGUAGE_STATE_SESSION_KEY_PREFIX}${courseId}`);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw) as { currentLanguage?: string; supportedLanguages?: string[] };
  } catch {
    return null;
  }
};

const loadPersistedTranslations = (courseId: string): Record<string, PersistedTranslationEntry> => {
  try {
    const raw = sessionStorage.getItem(`${LANGUAGE_TRANSLATIONS_SESSION_KEY_PREFIX}${courseId}`);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw) as Record<string, PersistedTranslationEntry>;
    return Object.entries(parsed).reduce<Record<string, PersistedTranslationEntry>>((acc, [languageCode, entry]) => {
      const normalized = normalizeLanguageCode(languageCode);
      if (normalized) {
        acc[normalized] = entry ?? {};
      }
      return acc;
    }, {});
  } catch {
    return {};
  }
};

const loadPersistedCurriculumSnapshots = (courseId: string): Record<string, PersistedSection[]> => {
  try {
    const raw = sessionStorage.getItem(`${CURRICULUM_SNAPSHOT_SESSION_KEY_PREFIX}${courseId}`);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw) as Record<string, PersistedSection[]>;
    return Object.entries(parsed).reduce<Record<string, PersistedSection[]>>((acc, [languageCode, sections]) => {
      const normalized = normalizeLanguageCode(languageCode);
      if (normalized) {
        acc[normalized] = Array.isArray(sections) ? sections : [];
      }
      return acc;
    }, {});
  } catch {
    return {};
  }
};

type CourseDetailLecture = CourseDetailSection['lectures'][number];

const mapContentType = (raw?: string | null): CourseDetailLecture['contentType'] | undefined => {
  const normalized = normalizeLanguageCode(raw) ?? '';
  const upper = normalized.replace(/[^a-z0-9]/gi, '_').toUpperCase();
  if (upper === 'VIDEO' || upper === 'MARKDOWN' || upper === 'VIDEO_MARKDOWN' || upper === 'QUIZ' || upper === 'EXTERNAL_LINK' || upper === 'TEXT' || upper === 'CODING') {
    return upper as CourseDetailLecture['contentType'];
  }
  return undefined;
};

const mapPlatformSupport = (raw?: string | null): CourseDetailLecture['platformSupport'] | undefined => {
  const normalized = normalizeLanguageCode(raw) ?? '';
  const upper = normalized.replace(/[^a-z0-9]/gi, '_').toUpperCase();
  if (upper === 'IDE_ONLY' || upper === 'WEB_ONLY' || upper === 'IDE_AND_WEB' || upper === 'UNKNOWN') {
    return upper as CourseDetailLecture['platformSupport'];
  }
  if (upper === 'IDEANDWEB') {
    return 'IDE_AND_WEB';
  }
  if (upper === 'IDE') {
    return 'IDE_ONLY';
  }
  if (upper === 'WEB') {
    return 'WEB_ONLY';
  }
  return undefined;
};

const convertPersistedSections = (
  sections: PersistedSection[] = [],
  fallback: CourseDetailSection[] = [],
): CourseDetailSection[] => {
  return sections.map((section, sectionIndex) => {
    const fallbackSection = fallback[sectionIndex];
    const sectionId = section.id ?? fallbackSection?.id ?? `section-${sectionIndex + 1}`;
    const lessonsSource: PersistedLesson[] = section.lessons && section.lessons.length > 0
      ? section.lessons
      : (fallbackSection?.lectures ?? []).map(lecture => ({
          id: lecture.id,
          title: lecture.title,
          order: lecture.order,
          duration: lecture.duration ?? undefined,
          contentType: lecture.contentType,
          platformSupport: lecture.platformSupport,
        }));

    const lectures: CourseDetailLecture[] = lessonsSource.map((lesson, lessonIndex) => {
      const fallbackLecture = fallbackSection?.lectures?.[lessonIndex];
      const lectureId = lesson.id ?? fallbackLecture?.id ?? `${sectionId}-lesson-${lessonIndex + 1}`;
      return {
        id: String(lectureId),
        title: lesson.title ?? fallbackLecture?.title ?? `Lecture ${lessonIndex + 1}`,
        order: lesson.order ?? fallbackLecture?.order ?? lessonIndex + 1,
        duration: lesson.duration ?? fallbackLecture?.duration ?? null,
        contentType: mapContentType(lesson.contentType ?? fallbackLecture?.contentType),
        platformSupport: mapPlatformSupport(lesson.platformSupport ?? fallbackLecture?.platformSupport),
        isCompleted: fallbackLecture?.isCompleted ?? null,
      };
    });

    return {
      id: String(sectionId),
      title: section.title ?? fallbackSection?.title ?? `Section ${sectionIndex + 1}`,
      description: section.description ?? fallbackSection?.description ?? null,
      order: section.order ?? fallbackSection?.order ?? sectionIndex + 1,
      lectures,
    };
  });
};

// CourseDetailDataForTemplate 인터페이스 삭제

const CourseDetailPage: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = usePrefixedNavigate();
  const location = useLocation();
  // 🚀 useApi 제거! 자동 생성된 훅으로 완전 자동화
  const queryClient = useQueryClient(); // queryClient 인스턴스 가져오기
  // Zustand 스토어 상태 및 액션 개별적으로 가져오기
  const previousEntryPoint = useNavigationStore((state: NavigationState) => state.previousEntryPoint);
  
  const clearPreviousEntryPoint = useNavigationStore((state: NavigationState) => state.clearPreviousEntryPoint);

  const { currentLocale } = useParaglideLocale();
  const currentLanguage = useMemo(() => (currentLocale ?? 'ko').split('-')[0], [currentLocale]);

  const effectiveCourseId = courseId ?? '';

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isPreviewMode = useMemo(() => {
    const previewRaw = searchParams.get('preview');
    return previewRaw === '1' || previewRaw?.toLowerCase() === 'true';
  }, [searchParams]);
  const previewLanguageParam = useMemo(() => normalizeLanguageCode(searchParams.get('previewLanguage')), [searchParams]);

  const courseQueryParams = useMemo(() => {
    if (!isPreviewMode) {
      return undefined;
    }
    return {
      preview: true,
      previewLanguage: previewLanguageParam ?? undefined,
    } as const;
  }, [isPreviewMode, previewLanguageParam]);

  const baseCourseQueryKey = useMemo(
    () => getGetCourseDetailQueryKey(effectiveCourseId, courseQueryParams),
    [effectiveCourseId, courseQueryParams]
  );

  const courseQueryKey = useMemo(() => {
    return [...baseCourseQueryKey, currentLanguage] as const;
  }, [baseCourseQueryKey, currentLanguage]);

  const {
    data: courseData,
    isLoading,
    error,
    refetch,
    queryKey: activeQueryKey,
  } = useGetCourseDetail<CourseDetail, unknown>(effectiveCourseId, courseQueryParams, {
    query: {
      enabled: Boolean(courseId),
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 30,
      retry: 1,
      queryKey: courseQueryKey,
    },
  });

  const [ownedOverride, setOwnedOverride] = useState<boolean | null>(null);
  const [languageOptions, setLanguageOptions] = useState<string[]>(['ko']);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('ko');
  const [translationOverrides, setTranslationOverrides] = useState<Record<string, PersistedTranslationEntry>>({});
  const [curriculumOverrides, setCurriculumOverrides] = useState<Record<string, PersistedSection[]>>({});

  useEffect(() => {
    if (!effectiveCourseId) {
      return;
    }

    const persistedState = loadPersistedLanguageState(effectiveCourseId);
    const persistedTranslations = loadPersistedTranslations(effectiveCourseId);
    const persistedSnapshots = loadPersistedCurriculumSnapshots(effectiveCourseId);

    setTranslationOverrides(persistedTranslations);
    setCurriculumOverrides(persistedSnapshots);

    const defaultLanguage = normalizeLanguageCode(courseData?.summary?.defaultLanguage) ?? 'ko';
    const collected = new Set<string>();

    const addLanguage = (code?: string | null) => {
      const normalized = normalizeLanguageCode(code);
      if (normalized) {
        collected.add(normalized);
      }
    };

    addLanguage(defaultLanguage);
    (courseData?.summary?.supportedLanguages ?? []).forEach(addLanguage);
    courseData?.translations?.forEach(entry => addLanguage(entry?.language));
    courseData?.localizedSections?.forEach(entry => addLanguage(entry?.language));
    (persistedState?.supportedLanguages ?? []).forEach(addLanguage);
    Object.keys(persistedTranslations).forEach(addLanguage);
    Object.keys(persistedSnapshots).forEach(addLanguage);
    addLanguage(previewLanguageParam);

    const languages = Array.from(collected);
    if (languages.length === 0) {
      languages.push(defaultLanguage);
    } else {
      languages.sort((a, b) => {
        if (a === defaultLanguage) return -1;
        if (b === defaultLanguage) return 1;
        return a.localeCompare(b);
      });
    }

    setLanguageOptions(languages);
    setSelectedLanguage(prev => {
      const preferred = previewLanguageParam
        ?? normalizeLanguageCode(persistedState?.currentLanguage)
        ?? normalizeLanguageCode(prev)
        ?? defaultLanguage;
      if (preferred && languages.includes(preferred)) {
        return preferred;
      }
      return languages[0] ?? defaultLanguage;
    });
  }, [
    effectiveCourseId,
    courseData?.summary?.defaultLanguage,
    courseData?.summary?.supportedLanguages,
    courseData?.translations,
    courseData?.localizedSections,
    previewLanguageParam,
  ]);

  const defaultLanguageFromApi = useMemo(
    () => normalizeLanguageCode(courseData?.summary?.defaultLanguage) ?? 'ko',
    [courseData?.summary?.defaultLanguage],
  );

  const apiTranslationMap = useMemo(() => {
    if (!courseData) {
      return {} as Record<string, PersistedTranslationEntry>;
    }

    const map: Record<string, PersistedTranslationEntry> = {};

    const ensureEntry = (languageCode?: string | null) => {
      const normalized = normalizeLanguageCode(languageCode);
      if (!normalized) {
        return null;
      }
      if (!map[normalized]) {
        map[normalized] = {};
      }
      return normalized;
    };

    const defaultKey = ensureEntry(defaultLanguageFromApi);
    if (defaultKey) {
      map[defaultKey] = {
        title: courseData.summary.title,
        description: courseData.description ?? courseData.summary.description ?? '',
      };
    }

    const applyTranslation = (languageCode?: string | null, title?: string | null, description?: string | null) => {
      const key = ensureEntry(languageCode);
      if (!key) {
        return;
      }
      const existing = map[key] ?? {};
      const next: PersistedTranslationEntry = { ...existing };
      if (title !== undefined && title !== null) {
        next.title = title;
      }
      if (description !== undefined && description !== null) {
        next.description = description;
      }
      map[key] = next;
    };

    (courseData.summary.supportedLanguages ?? []).forEach(languageCode => {
      ensureEntry(languageCode);
    });

    courseData.translations?.forEach(entry => {
      applyTranslation(entry?.language, entry?.title ?? null, entry?.description ?? null);
    });

    courseData.summary.metadataTranslations?.forEach(entry => {
      applyTranslation(entry?.language, entry?.title ?? null, entry?.description ?? null);
    });

    return map;
  }, [courseData, defaultLanguageFromApi]);

  const mergedTranslationMap = useMemo(() => {
    const combined: Record<string, PersistedTranslationEntry> = { ...apiTranslationMap };
    Object.entries(translationOverrides).forEach(([languageCode, entry]) => {
      const normalized = normalizeLanguageCode(languageCode);
      if (!normalized) {
        return;
      }
      combined[normalized] = {
        ...combined[normalized],
        ...entry,
      };
    });
    return combined;
  }, [apiTranslationMap, translationOverrides]);

  const apiCurriculumMap = useMemo(() => {
    if (!courseData) {
      return {} as Record<string, CourseDetailSection[]>;
    }

    const baseSections = (courseData.sections ?? []).map((section) => ({
      ...section,
      lectures: (section.lectures ?? []).map((lecture) => ({ ...lecture })),
    }));

    const map: Record<string, CourseDetailSection[]> = {
      [normalizeLanguageCode(courseData.summary.defaultLanguage) ?? 'ko']: baseSections,
    };

    (courseData.localizedSections ?? []).forEach((entry) => {
      const normalized = normalizeLanguageCode(entry?.language);
      if (!normalized) {
        return;
      }

      const localizedSections = entry?.sections ?? [];
      const merged = baseSections.map((baseSection, sectionIndex) => {
        const localizedSection = localizedSections[sectionIndex];
        const mergedLectures = (baseSection.lectures ?? []).map((baseLecture, lectureIndex) => {
          const localizedLecture = localizedSection?.lectures?.[lectureIndex];
          return {
            ...baseLecture,
            title: localizedLecture?.title ?? baseLecture.title,
            description: localizedLecture?.description ?? baseLecture.description,
            contentType: localizedLecture?.contentType ?? baseLecture.contentType,
            duration: localizedLecture?.duration ?? baseLecture.duration,
            videoUrl: localizedLecture?.videoUrl ?? baseLecture.videoUrl,
            contentUrl: localizedLecture?.contentUrl ?? baseLecture.contentUrl,
            platformSupport: localizedLecture?.platformSupport ?? baseLecture.platformSupport,
            language: localizedLecture?.language ?? normalized,
          };
        });

        return {
          ...baseSection,
          title: localizedSection?.title ?? baseSection.title,
          description: localizedSection?.description ?? baseSection.description,
          lectures: mergedLectures,
        };
      });

      map[normalized] = merged;
    });

    return map;
  }, [courseData]);

  const displayCourseDetail = useMemo(() => {
    if (!courseData) {
      return null;
    }

    const activeLanguage = selectedLanguage && languageOptions.includes(selectedLanguage)
      ? selectedLanguage
      : languageOptions[0] ?? 'ko';

    const fallbackTranslation = mergedTranslationMap[defaultLanguageFromApi];
    const translationEntry = mergedTranslationMap[activeLanguage] ?? fallbackTranslation;

    const baseDescription = fallbackTranslation?.description
      ?? courseData.description
      ?? courseData.summary?.description
      ?? '';
    const displayDescription = translationEntry?.description && translationEntry.description.trim().length > 0
      ? translationEntry.description
      : baseDescription;

    const baseTitle = fallbackTranslation?.title ?? courseData.summary.title;
    const displayTitle = translationEntry?.title && translationEntry.title.trim().length > 0
      ? translationEntry.title
      : baseTitle;

    const persistedSections = curriculumOverrides[activeLanguage];
    const fallbackSections = apiCurriculumMap[activeLanguage] ?? courseData.sections ?? [];
    const displaySections = persistedSections && persistedSections.length > 0
      ? convertPersistedSections(persistedSections, fallbackSections)
      : fallbackSections;

    return {
      ...courseData,
      description: displayDescription,
      summary: {
        ...courseData.summary,
        title: displayTitle,
        description: displayDescription,
      },
      sections: displaySections,
    } as CourseDetail;
  }, [
    courseData,
    selectedLanguage,
    mergedTranslationMap,
    curriculumOverrides,
    apiCurriculumMap,
    languageOptions,
    defaultLanguageFromApi,
  ]);

  useEffect(() => {
    const backendOwned = courseData?.summary?.owned === true;
    if (shouldClearOwnedOverride(backendOwned, ownedOverride)) {
      setOwnedOverride(null);
    }
  }, [courseData?.summary?.owned, ownedOverride]);

  const applyOwnedUpdate = useCallback(
    (owned: boolean) =>
      (previous: CourseDetail | undefined) => {
        if (!previous || !previous.summary || previous.summary.owned === owned) {
          return previous;
        }

        return {
          ...previous,
          summary: {
            ...previous.summary,
            owned,
          },
        };
      },
    []
  );

  const updateOwnedStatus = useCallback(
    (owned: boolean) => {
      queryClient.setQueryData<CourseDetail | undefined>(courseQueryKey, applyOwnedUpdate(owned));
      queryClient.setQueryData<CourseDetail | undefined>(baseCourseQueryKey, applyOwnedUpdate(owned));
    },
    [applyOwnedUpdate, baseCourseQueryKey, courseQueryKey, queryClient]
  );

  const invalidateCourseDetail = useCallback(() => {
    if (!effectiveCourseId || !activeQueryKey) {
      return;
    }

    queryClient.invalidateQueries({ queryKey: activeQueryKey });
    queryClient.invalidateQueries({ queryKey: getGetCourseDetailQueryKey(effectiveCourseId) });
    void refetch();
  }, [activeQueryKey, effectiveCourseId, queryClient, refetch]);

  // 뒤로가기 핸들러 (Zustand 사용)
  const handleGoBack = () => {
    console.log('[CourseDetailPage] Go back requested. Previous entry point from store:', previousEntryPoint);

    if (previousEntryPoint) {
      console.log('[CourseDetailPage] Navigating back to previous entry point:', previousEntryPoint);
      navigate(previousEntryPoint);
      // 상태 초기화: 성공적으로 이전 페이지로 돌아갔으므로 진입점 정보 제거
      clearPreviousEntryPoint();
      return;
    }

    // 전역 상태에 진입점 정보가 없으면 기본 뒤로가기 동작
    console.log('[CourseDetailPage] Previous entry point not found in store, using navigate(-1).');
    navigate(-1);
  };

  // 수강 신청 성공 시 호출될 콜백 함수 정의
  const handleEnrollmentSuccess = () => {
    console.log('[CourseDetailPage] Enrollment successful, invalidating course query.');
    setOwnedOverride(true);
    updateOwnedStatus(true);

    // Immediately update the cache to set owned to true
    queryClient.setQueryData<CourseDetail>(courseQueryKey, (previous) => {
      if (!previous) return previous;
      return {
        ...previous,
        summary: {
          ...previous.summary,
          owned: true
        }
      };
    });

    // Also invalidate to ensure fresh data
    invalidateCourseDetail();
  };

  // 수강 취소 성공 시 호출될 콜백 함수 정의
  const handleCancelSuccess = () => {
    console.log('[CourseDetailPage] Cancellation successful, invalidating course query.');
    setOwnedOverride(false);
    updateOwnedStatus(false);

    // Immediately update the cache to set owned to false
    queryClient.setQueryData<CourseDetail>(courseQueryKey, (previous) => {
      if (!previous) return previous;
      return {
        ...previous,
        summary: {
          ...previous.summary,
          owned: false
        }
      };
    });

    // Also invalidate to ensure fresh data
    invalidateCourseDetail();
  };

  const handleAuthorClick = () => {
    const username = courseData?.summary?.instructor?.basicInfo?.username;

    if (!username) {
      console.warn('[CourseDetailPage] Cannot navigate to instructor profile: username missing.', {
        instructor: courseData?.summary?.instructor,
      });
      return;
    }

    navigate(`/profile/${username}`);
  };

  // --- Loading State Handling ---
  if (isLoading) {
    return <LoadingScreen message={course_courseDetail_loading()} />;
  }

  // --- Error State Handling ---
  if (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.error('[CourseDetailPage] Failed to load course detail:', errorMessage, error);
    return <ErrorDisplay message={`${course_courseDetail_error()} (${errorMessage})`} />;
  }

  // --- Data Validation (after loading and error checks) ---
  // courseData 자체가 있는지, 그리고 summary가 있는지 확인
  if (!courseData || !courseData.summary) {
    // Log the problematic data for debugging
    console.error("Course data is missing or incomplete after fetch:", courseData);
    // 좀 더 구체적인 에러 메시지 표시 가능
    return <ErrorDisplay message={course_courseDetail_dataError()} />;
  }

  const resolvedCourseDetail = displayCourseDetail ?? courseData;
  const availableLanguageOptions = languageOptions.length > 0 ? languageOptions : ['ko'];
  const templateSelectedLanguage = selectedLanguage && availableLanguageOptions.includes(selectedLanguage)
    ? selectedLanguage
    : availableLanguageOptions[0];

  if (!resolvedCourseDetail) {
    return null;
  }

  return (
    <div>
      <PageHeader
        title={resolvedCourseDetail.summary?.title || course_title()}
        showBackButton
        onBackClick={handleGoBack}
      />
      <CourseDetailTemplate
        courseDetail={resolvedCourseDetail}
        ownedOverride={ownedOverride}
        onEnrollmentSuccess={handleEnrollmentSuccess}
        onCancelSuccess={handleCancelSuccess}
        onAuthorClick={handleAuthorClick}
        availableLanguages={availableLanguageOptions}
        selectedLanguage={templateSelectedLanguage}
        onLanguageChange={(language) => setSelectedLanguage(coerceLanguageCode(language))}
        isPreviewMode={isPreviewMode}
      />
    </div>
  );
};

export default CourseDetailPage; 
